import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    CircularProgress,
    Dialog,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Close } from "@mui/icons-material";

export interface LightboxImage {
    id: string;
    /** Full-size source shown in the viewer. */
    src: string;
    alt: string;
}

interface ImageLightboxProps {
    images: LightboxImage[];
    /** Index of the open image, or null when closed. */
    index: number | null;
    onIndexChange: (index: number) => void;
    onClose: () => void;
    /** Buttons for the current image (e.g. copy, download). */
    renderActions?: (image: LightboxImage, index: number) => React.ReactNode;
}

const SWIPE_THRESHOLD_PX = 50;

/**
 * Full-size image viewer: full screen on phones, ←/→ keys and swipe to
 * move between images, Esc to close.
 */
export const ImageLightbox: React.FC<ImageLightboxProps> = ({
    images,
    index,
    onIndexChange,
    onClose,
    renderActions,
}) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const [loaded, setLoaded] = useState(false);
    const touchStartX = useRef<number | null>(null);

    const open = index !== null && index >= 0 && index < images.length;
    const current = open ? images[index as number] : null;
    const count = images.length;

    useEffect(() => {
        setLoaded(false);
    }, [current?.src]);

    const go = (delta: number) => {
        if (!open || count < 2) return;
        onIndexChange(((index as number) + delta + count) % count);
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "ArrowRight") {
            e.preventDefault();
            go(1);
        } else if (e.key === "ArrowLeft") {
            e.preventDefault();
            go(-1);
        }
    };

    const onTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0]?.clientX ?? null;
    };
    const onTouchEnd = (e: React.TouchEvent) => {
        if (touchStartX.current === null) return;
        const dx = (e.changedTouches[0]?.clientX ?? touchStartX.current) - touchStartX.current;
        touchStartX.current = null;
        if (Math.abs(dx) >= SWIPE_THRESHOLD_PX) go(dx < 0 ? 1 : -1);
    };

    const navButtonSx = {
        position: "absolute" as const,
        top: "50%",
        transform: "translateY(-50%)",
        bgcolor: "rgba(0,0,0,0.45)",
        color: "common.white",
        width: 48,
        height: 48,
        "&:hover": { bgcolor: "rgba(0,0,0,0.65)" },
        display: { xs: "none", sm: "inline-flex" },
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={fullScreen}
            maxWidth="lg"
            fullWidth
            onKeyDown={onKeyDown}
            aria-label={current ? `Image ${(index as number) + 1} of ${count}: ${current.alt}` : "Image viewer"}
            PaperProps={{ sx: { bgcolor: "grey.900", color: "common.white", overflow: "hidden" } }}
        >
            {current && (
                <Stack sx={{ height: fullScreen ? "100%" : "calc(100vh - 64px)", maxHeight: fullScreen ? "none" : 900 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1, minHeight: 56 }}>
                        <Typography variant="body2" sx={{ flex: 1, minWidth: 0, opacity: 0.85 }} noWrap title={current.alt}>
                            {count > 1 ? `${(index as number) + 1} / ${count} · ` : ""}
                            {current.alt}
                        </Typography>
                        <IconButton onClick={onClose} aria-label="Close" sx={{ color: "common.white" }}>
                            <Close />
                        </IconButton>
                    </Stack>

                    <Box
                        onTouchStart={onTouchStart}
                        onTouchEnd={onTouchEnd}
                        sx={{
                            position: "relative",
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            px: { xs: 0, sm: 8 },
                            touchAction: "pan-y pinch-zoom",
                        }}
                    >
                        {!loaded && <CircularProgress sx={{ position: "absolute", color: "grey.400" }} />}
                        <Box
                            key={current.src}
                            component="img"
                            src={current.src}
                            alt={current.alt}
                            onLoad={() => setLoaded(true)}
                            // Cached images can finish before onLoad is attached.
                            ref={(el: HTMLImageElement | null) => {
                                if (el?.complete && el.naturalWidth > 0) setLoaded(true);
                            }}
                            sx={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                                opacity: loaded ? 1 : 0,
                                transition: "opacity 120ms",
                                userSelect: "none",
                            }}
                            draggable={false}
                        />
                        {count > 1 && (
                            <>
                                <IconButton onClick={() => go(-1)} aria-label="Previous image" sx={{ ...navButtonSx, left: 12 }}>
                                    <ChevronLeft />
                                </IconButton>
                                <IconButton onClick={() => go(1)} aria-label="Next image" sx={{ ...navButtonSx, right: 12 }}>
                                    <ChevronRight />
                                </IconButton>
                            </>
                        )}
                    </Box>

                    {(renderActions || count > 1) && (
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="center"
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ px: 1.5, py: 1.5, pb: "max(12px, env(safe-area-inset-bottom))" }}
                        >
                            {count > 1 && (
                                <IconButton
                                    onClick={() => go(-1)}
                                    aria-label="Previous image"
                                    sx={{ color: "common.white", display: { xs: "inline-flex", sm: "none" } }}
                                >
                                    <ChevronLeft />
                                </IconButton>
                            )}
                            {renderActions?.(current, index as number)}
                            {count > 1 && (
                                <IconButton
                                    onClick={() => go(1)}
                                    aria-label="Next image"
                                    sx={{ color: "common.white", display: { xs: "inline-flex", sm: "none" } }}
                                >
                                    <ChevronRight />
                                </IconButton>
                            )}
                        </Stack>
                    )}
                </Stack>
            )}
        </Dialog>
    );
};
